import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAutoSave } from "@/hooks/useAutoSave";
import { Mail } from "lucide-react";
import SectionHeader from "../shared/SectionHeader";
import ZapierIntegrationCard from "@/components/integrations/ZapierIntegrationCard";

interface AgentPostChatTabProps {
  agent: any;
  updateAgent: (data: Partial<any>) => Promise<boolean>;
}

// Create schemas for form sections
const emailSchema = z.object({
  notification_email: z.string().optional(),
});

const webhookSchema = z.object({
  notification_webhook_url: z.string().optional(),
});

type EmailFormValues = z.infer<typeof emailSchema>;
type WebhookFormValues = z.infer<typeof webhookSchema>;

const AgentPostChatTab = ({ agent, updateAgent }: AgentPostChatTabProps) => {
  const [initialFormValues, setInitialFormValues] = useState<any | null>({
    notification_email: agent.notification_email || "",
    notification_webhook_url: agent.notification_webhook_url || "",
  });

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      notification_email: agent.notification_email || "",
    },
  });

  const webhookForm = useForm<WebhookFormValues>({
    resolver: zodResolver(webhookSchema),
    defaultValues: {
      notification_webhook_url: agent.notification_webhook_url || "",
    },
  });

  const { isSaving, setDirty } = useAutoSave({
    onSave: async () => {
      if (!initialFormValues) return;
      const emailData = emailForm.getValues();
      const webhookData = webhookForm.getValues();

      const data = {
        ...emailData,
        ...webhookData,
      };

      return await updateAgent(data);
    },
    successMessage: "Notification settings saved automatically",
    errorMessage: "Failed to auto-save notification settings",
  });

  // Set up form change subscriptions for auto-save
  useEffect(() => {
    if (!initialFormValues) return;
    const handleChange = () => {
      const emailData = emailForm.getValues();
      const webhookData = webhookForm.getValues();
      const currentValues = {
        ...emailData,
        ...webhookData,
      };
      const hasChanges = Object.keys(currentValues).some(
        (key) => currentValues[key] !== initialFormValues[key]
      );
      if (hasChanges) {
        setInitialFormValues(currentValues);
        setDirty();
      }
    };
    const emailSubscription = emailForm.watch(handleChange);
    const webhookSubscription = webhookForm.watch(handleChange);
    return () => {
      emailSubscription.unsubscribe();
      webhookSubscription.unsubscribe();
    };
  }, [
    emailForm,
    webhookForm,
    setDirty,
    initialFormValues,
  ]);

  return (
    <div className="w-full space-y-6">
      {/* Notifications Section */}
      <Card className="border rounded-2xl">
        <CardContent className="p-5">
          <SectionHeader
            icon={<Mail className="h-5 w-5" />}
            title="Notifications"
          />

          <div className="space-y-4">
            <Form {...emailForm}>
              <form className="space-y-4">
                <FormField
                  control={emailForm.control}
                  name="notification_email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Email for Notifications
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter email for notifications"
                          className="focus:border-[#060520] focus:ring-1 focus:ring-[#060520]"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <p className="text-xs text-gray-500 mt-2">
                        Receive email notifications when users interact with
                        your agent.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>

            <Form {...webhookForm}>
              <form className="space-y-4">
                <FormField
                  control={webhookForm.control}
                  name="notification_webhook_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Webhook URL
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://api.example.com/webhook"
                          className="focus:border-[#060520] focus:ring-1 focus:ring-[#060520]"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <p className="text-xs text-gray-500 mt-2">
                        Send webhook notifications to your system when
                        conversations are completed. Your endpoint will receive
                        JSON data about the conversation.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>
        </CardContent>
      </Card>

      {/* Information Section */}
      <Card className="border rounded-2xl bg-gray-50">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Mail className="h-5 w-5 text-primary mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-primary mb-2">
                About Notifications
              </h4>
              <div className="text-sm text-gray-600 space-y-2">
                <p>
                  • <strong>Email notifications</strong> include conversation summaries, user information, and form field data
                </p>
                <p>
                  • <strong>Webhook notifications</strong> send structured JSON data to your endpoint in real-time
                </p>
                <p>
                  • Both notification types are triggered when a conversation is completed by the user
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Zapier Integration */}
      <ZapierIntegrationCard 
        title="Zapier Automation"
        description="No-code automation for this agent's completed conversations"
        compact={true}
      />

      {/* Auto-save indicator */}
      {isSaving && (
        <div className="text-sm text-gray-500 text-center">
          Saving changes...
        </div>
      )}
    </div>
  );
};

export default AgentPostChatTab;
